<script>
import { includes } from 'lodash-es';
import IconTotemFail from '@/components/IconTotemFail.vue';
import IconTotemSuccess from '@/components/IconTotemSuccess.vue';
import IconCardCracked from '@/components/IconCardCracked.vue';
import IconCardFail from '@/components/IconCardFail.vue';
import IconCardSuccess from '@/components/IconCardSuccess.vue';
import IconLetsPlay from '@/components/IconLetsPlay.vue';
import UiTextField from '@/components/UiTextField.vue';
import getErrorCodeTranslation from '@/helpers/getErrorCodeTranslation';

export default {
  name: 'ActionResult',

  components: {
    IconTotemFail,
    IconTotemSuccess,
    IconCardCracked,
    IconCardFail,
    IconCardSuccess,
    IconLetsPlay,
    UiTextField,
  },

  props: {
    type: {
      required: true,
      type: String,
      validator(value) {
        return includes(['noMoneyError', 'unknownError', 'customError', 'threeDsError', 'success', 'ach_success'], value);
      },
    },

    code: {
      type: String,
    },

    orderId: {
      required: true,
      type: String,
    },

    platformInstructionLink: {
      default: '',
      type: String,
    },

    email: {
      required: true,
      type: String,
    },
  },

  computed: {
    types() {
      return {
        // 1. Охуевший тотем - нахватка средств или специальная ошибка (+ жёлтый текст и опции)
        // 2. Карта с крестиком - неведомая ошибка
        // 3. Разбитая карта - общая ошибка с текстом
        noMoneyError: {
          title: this.$t('ActionResult.noMoneyError.title'),
          subtitle: this.$t('ActionResult.noMoneyError.subtitle'),
          iconComponent: 'IconTotemFail',
          description: this.$t('ActionResult.noMoneyError.description'),
        },
        unknownError: {
          title: this.$t('ActionResult.unknownError.title'),
          subtitle: this.$t('ActionResult.unknownError.subtitle'),
          iconComponent: 'IconCardFail',
          description: this.$t('ActionResult.unknownError.description'),
        },
        customError: {
          subtitle: this.$t('ActionResult.customError.subtitle'),
          iconComponent: 'IconCardCracked',
          description: this.$t('ActionResult.customError.description'),
        },
        // 3DS authentication couldn't be completed (timeout / transport error /
        // unauthenticated) in required mode. Copy is hardcoded by request — this type
        // is dispatched without a `code`, so the description below is what renders.
        threeDsError: {
          title: 'Authentication needed',
          subtitle: 'An error occurred.',
          iconComponent: 'IconCardCracked',
          description: 'We couldn’t verify your card with 3-D Secure. Please try again or use a different card.',
        },
        success: {
          descriptionSlaveInitial: this.$t('ActionResult.success.descriptionSlaveInitial'),
          email: this.$t('ActionResult.success.email'),
          iconComponent: 'IconTotemSuccess',
        },
        ach_success: {
          descriptionSlaveInitial: this.$t('ActionResult.success.descriptionSlaveInitial'),
          email: this.$t('ActionResult.success.email'),
          iconComponent: 'IconACHSuccess',
        }
      };
    },
    message() {
      return getErrorCodeTranslation(this.code, this.$i18n.locale);
    },
  },
  created() {
    this.$addCssRules({
      [`.${this.$style.titleMain}`]: {
        color: this.$gui.resultTitleColor,
      },
      [`.${this.$style.titleSub}`]: {
        color: this.$gui.resultSubtitleColor,
      },
      [`.${this.$style.description}`]: {
        color: this.$gui.resultTextColor,
      },
      [`
        .${this.$style.titleSubSlave},
        .${this.$style.titleSubSlave} a,
        .${this.$style.descriptionSlave}
      `]: {
        color: this.$gui.resultSubtextColor,
      },
      [`.${this.$style.code}`]: {
        color: this.$gui.resultCodeColor,
      },
      [`.${this.$style.email}`]: {
        color: this.$gui.resultEmailColor,
      },
    });
  },
};
</script>

<template>
  <div :class="$style.actionResult">
    <div>
      <h2
        :class="$style.titleMain"
        v-html="types[type].title"
      ></h2>
    </div>
    <div :class="$style.icon">
      <component
        :is="types[type].iconComponent"
        :class="$style.iconComponent"
      />
    </div>
    <div v-if="type === 'success'">
      <template v-if="false">
        <p
          :class="$style.descriptionSlave"
          v-html="types[type].descriptionSlaveInitial"
        ></p>
      </template>
    </div>
    <div
      :class="$style.description"
      v-else
    >
      <span v-html="this.code ? message : types[type].description"></span>
    </div>
  </div>
</template>

<style lang="scss" module>
.actionResult {
  text-align: center;
  display: flex;
  flex-direction: column;
  padding: 12px 0 20px;
  width: 100%;
}

.titleMain {
  font-weight: bold;
  font-size: 30px;
  line-height: 31px;
  margin: 0 0 10px;
}

.titleSub {
  font-weight: 500;
  font-size: 15px;
  line-height: 23px;
}

.titleSubSlave {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 5px;
}

.icon {
  display: flex;
  justify-content: center;
  align-items: center;
}

.iconComponent {
  animation: {
    name: showIcon;
    duration: 0.4s;
    timing-function: ease;
    iteration-count: 1;
    fill-mode: forwards;
  }
}

.code {
  font-weight: bold;
  font-size: 12px;
  line-height: 22px;
  margin: 0 -20px;
}

.description {
  font-weight: 500;
  font-size: 15px;
  line-height: 23px;
}

.descriptionSlave {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
}

.email {
  font-weight: bold;
  font-size: 15px;
  line-height: 19px;
  margin-top: 8px;
}

@keyframes showIcon {
  0% {
    max-height: 0;
  }
  100% {
    max-height: 100%;
  }
}
</style>
